import type { FunctionComponent, ReactNode } from "react";
import { Fragment } from "react";
import { generatePath, NavLink } from "react-router-dom";

import TableHeader from "~/components/Table/components/TableHeader";
import TableHeaderCell from "~/components/Table/components/TableHeaderCell";
import TableWrapper from "~/components/Table/components/TableWrapper";
import { PROJECT_DETAILS_PATH_WITH_DEFAULT_TAB_ID, TIME_TRACKINGS_PATH } from "~/constants/appRoutes.ts";
import type {
	TeamUtilisationMonth,
	TeamUtilisationRow,
} from "~/modules/reports/api/utilisationReport/utilisationReportTypes.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

export type ResolvedProjectRow = TeamUtilisationRow & {
	label: string;
};

export type ResolvedMember = {
	staffMemberId: string;
	userId: string;
	name: string;
	mainRow?: TeamUtilisationRow;
	projectRows: ResolvedProjectRow[];
	internalRow?: TeamUtilisationRow;
	salesRow?: TeamUtilisationRow;
};

export type ResolvedGroup = {
	careerLevelId: string;
	displayName: string;
	members: ResolvedMember[];
};

type Props = {
	groups: ResolvedGroup[];
	months: TeamUtilisationMonth[];
	showProjects: boolean;
	showSalesAndInternal: boolean;
};

const cellFor = (row: TeamUtilisationRow | undefined, month: TeamUtilisationMonth) =>
	row?.cells.find((cell) => cell.year === month.year && cell.month === month.month);

const timeTrackingPath = (userId: string, month: TeamUtilisationMonth) =>
	generatePath(TIME_TRACKINGS_PATH, { userId }) + `?year=${month.year}&month=${month.month}`;

const renderValues = (averageDaysPerWeek?: number, daysTracked?: number): ReactNode => {
	const averageText = averageDaysPerWeek ? formatNumberWithComma(averageDaysPerWeek, 2) : "--";
	const trackedText = daysTracked ? formatNumberWithComma(daysTracked, 2) : "--";

	if (averageText === "--" && trackedText === "--") {
		return <span className="text-gray-400">--</span>;
	}

	return <div className="flex flex-col text-sm">
		<span>{averageText}</span>
		<span className="text-xs text-gray-500">{trackedText}</span>
	</div>;
};

const TeamUtilisationTable: FunctionComponent<Props> = ({ groups, months, showProjects, showSalesAndInternal }) => {
	const labelClass = "z-10 border-b border-gray-100 px-3 py-2 text-left pl-6";
	const memberLabelClass = "z-10 border-b border-gray-100 px-3 py-2 text-left font-semibold";

	const dataCell = (userId: string, month: TeamUtilisationMonth, content: ReactNode, key: string) =>
		<td key={key}
			className="border-b border-gray-100 p-0 text-right tabular-nums">
			<NavLink to={timeTrackingPath(userId, month)}
					 className="block px-3 py-2 hover:underline">
				{content}
			</NavLink>
		</td>;

	const mainValues = (member: ResolvedMember, month: TeamUtilisationMonth) => {
		const mainCell = cellFor(member.mainRow, month);

		if (showSalesAndInternal) {
			return renderValues(mainCell?.averageDaysPerWeek, mainCell?.daysTracked);
		}

		const internalCell = cellFor(member.internalRow, month);
		const salesCell = cellFor(member.salesRow, month);
		const average = (mainCell?.averageDaysPerWeek ?? 0) - (internalCell?.averageDaysPerWeek ?? 0) - (salesCell?.averageDaysPerWeek ?? 0);
		const tracked = (mainCell?.daysTracked ?? 0) - (internalCell?.daysTracked ?? 0) - (salesCell?.daysTracked ?? 0);
		return renderValues(average, tracked);
	};

	return <div className="size-full overflow-auto">
		<TableWrapper rounded={true}>
			<TableHeader>
				<tr>
					<TableHeaderCell>
						Name
					</TableHeaderCell>
					{months.map((month) => <TableHeaderCell key={`${month.year}-${month.month}`}>
						{month.month}
					</TableHeaderCell>)}
				</tr>
				<tr className="h-[0.5px]">
					<td colSpan={months.length + 1} className="bg-gray-200" />
				</tr>
			</TableHeader>
			<tbody>
			{groups.map((group) => <Fragment key={group.careerLevelId}>
				<tr>
					<td colSpan={months.length + 1}
						className="left-0 border-b border-gray-200 bg-primary-500 px-3 py-1 text-sm text-white">
						{group.displayName}
					</td>
				</tr>
				{group.members.map((member) => <Fragment key={member.staffMemberId}>
					<tr className="hover:bg-gray-50">
						<td className={memberLabelClass}>{member.name}</td>
						{months.map((month) => dataCell(member.userId, month, mainValues(member, month), `${member.staffMemberId}-main-${month.year}-${month.month}`))}
					</tr>
					{showProjects && member.projectRows.map((row, rowIndex) =>
						<tr key={`${member.staffMemberId}-project-${row.projectId ?? rowIndex}`} className="hover:bg-gray-50">
							<td className={labelClass}>
								{row.projectId
									?
									<NavLink to={generatePath(PROJECT_DETAILS_PATH_WITH_DEFAULT_TAB_ID, { projectId: row.projectId })}
											 className="hover:underline">
										{row.label}
									</NavLink>
									: row.label}
							</td>
							{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(row, month)?.averageDaysPerWeek, cellFor(row, month)?.daysTracked), `${member.staffMemberId}-project-${row.projectId ?? rowIndex}-${month.year}-${month.month}`))}
						</tr>)}
					{showSalesAndInternal && <tr>
						<td className={labelClass}>Intern</td>
						{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(member.internalRow, month)?.averageDaysPerWeek, cellFor(member.internalRow, month)?.daysTracked), `${member.staffMemberId}-internal-${month.year}-${month.month}`))}
					</tr>}
					{showSalesAndInternal && <tr>
						<td className={labelClass}>Sales</td>
						{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(member.salesRow, month)?.averageDaysPerWeek, cellFor(member.salesRow, month)?.daysTracked), `${member.staffMemberId}-sales-${month.year}-${month.month}`))}
					</tr>}
				</Fragment>)}
			</Fragment>)}
			</tbody>
		</TableWrapper>
	</div>;
};

export default TeamUtilisationTable;
